(ns app.front.view.group.admin.config.verdict-defaults-config
  (:require [app.domain.group.config.model :as config-model]
            [app.domain.licence.model :as licence-model]
            [app.domain.util :as du]
            [app.domain.verdict.model :as verdict-model]
            [app.domain.verdict.section :as verdict-section]
            [app.front.entities :as entities]
            [app.front.ui :as ui]
            [app.front.ui.config-pills :as ui-config-pills]
            [app.front.ui.radio :as ui-radio]
            [app.front.ui.select :as ui-select]
            [app.front.util :as futil :refer [for-frag]]
            [app.i18n.model :as i18n-model]
            [clojure.string :as str]
            [core.db.entity.tree :as entity-tree]
            [core.db.entity.util :as eu]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub]]
            [core.front.feature-flags :as ff]
            [core.front.icons :as i]
            [core.user.model :as user-model]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]))

(rf/reg-sub ::configurable-sections
  :-> ::configurable-sections)

(rf/reg-sub ::authority-user-default-candidates
  :-> ::authority-user-default-candidates)

(rf/reg-sub ::authority-user-default-candidates-for-group
  :<- [::authority-user-default-candidates]
  (fn [all-candidates [_ group-id]]
    (get all-candidates group-id)))

(rf/reg-sub ::configurable-sections-for-group
  :<- [::configurable-sections]
  (fn [all-sections [_ group-id]]
    (get all-sections group-id)))

(rf/reg-sub ::configs-with-defaults
  :<- [::entities/entity-db]
  (fn [entity-db _]
    (<sub [::entities/entities (->> entity-db
                                    (filter (comp :group.config/verdict-defaults second))
                                    keys
                                    set)])))

(rf/reg-sub ::child-config-configurations
  :<- [::configs-with-defaults]
  (fn [configs [_ config-id]]
    (->> configs
         (remove #(= config-id (:db/id %)))
         (filter #(-> %
                      entity-tree/eager-ancestors
                      du/entity-id-set
                      (contains? config-id)))
         (mapcat (fn [config]
                   (map (fn [default]
                          [(config-model/verdict-default-identity default) config])
                        (:group.config/verdict-defaults config))))
         (group-by first)
         (p/map-vals #(mapv second %)))))

(rf/reg-sub ::addable-sections-for-group
  (fn [[_ _ group-id _]]
    (rf/subscribe [::configurable-sections-for-group group-id]))
  (fn [sections [_ _ _ already-selected]]
    (->> sections
         (remove (comp already-selected key))
         (into {}))))

(rf/reg-event-fx ::fetch-configurable-sections
  (fn [{:keys [db]} [_ group-id]]
    {:db           (-> db
                       (update ::configurable-sections assoc group-id nil))
     :comm/command {:command    :group/get-verdict-sections-with-defaults
                    :method     :get
                    :data       (util/mmap group-id)
                    :on-success [::fetch-configurable-sections.success group-id]}}))

(rf/reg-event-db ::fetch-configurable-sections.success
  (fn [db [_ group-id status resp]]
    (if (= 200 status)
      (-> db
          (assoc-in [::configurable-sections group-id] resp))
      db)))

(rf/reg-event-fx ::fetch-authority-user-default-candidates
  (fn [{:keys [db]} [_ group-id]]
    {:db           (-> db
                       (update ::authority-user-default-candidates assoc group-id nil))
     :comm/command {:command    :group/get-authority-user-default-candidates
                    :method     :get
                    :data       (util/mmap group-id)
                    :on-success [::fetch-authority-user-default-candidates.success group-id]}}))

(rf/reg-event-db ::fetch-authority-user-default-candidates.success
  (fn [db [_ group-id status resp]]
    (if (= 200 status)
      (-> db
          (assoc-in [::authority-user-default-candidates group-id] resp))
      db)))

(rf/reg-sub ::loading?
  :<- [::configurable-sections]
  (fn [sections [_ group-id]]
    (and (contains? sections group-id)
         (nil? (get sections group-id)))))

(rf/reg-sub ::section-def
  (fn [[_ group-id _ _]]
    (rf/subscribe [::configurable-sections-for-group group-id]))
  (fn [sections [_ _ section-id section-type]]
    (let [key-tuple [section-id section-type]]
      [key-tuple (get sections key-tuple)])))

(defmulti editor-for-type
  (fn [_ {:verdict-default-config/keys [applies-to]}]
    applies-to))

(defmethod editor-for-type :default [_ _] nil)

(defn- rule->amount-tr [rule]
  (case rule
    :verdict-default-config.date-input.rule/plus-days
    :verdict-default-config.date-input/amount-days
    :verdict-default-config.date-input.rule/months
    :verdict-default-config.date-input/amount-months
    :verdict-default-config.date-input.rule/end-of-year
    :verdict-default-config.date-input/amount-years
    :verdict-default-config.date-input.rule/day-of-year
    :verdict-default-config.date-input/days-after

    ;; default
    :verdict-default-config.date-input/amount))

(defmulti date-input-editor
  (fn [date-input-rule]
    date-input-rule))

(defmethod date-input-editor :verdict-default-config.date-input.rule/day-of-year
  [rule tr on-change amount config month day id years no-backdating?]
  (if on-change
    [:div
     [:div.inline-flex.gap-4
      [ui-select/select {:id         (str id "-month-selector")
                         :label-text (tr [:group-admin/licence-renewal-month])
                         :on-change  (fn [month]
                                       (on-change (assoc config :verdict-default-config.date-input/month month)))
                         :values     (range 12)
                         :val->str   #(tr [(keyword "date.month" (str %))])
                         :value      month
                         :required   true}]
      [ui-select/select {:id         (str id "-day-selector")
                         :label-text (tr [:group-admin/licence-renewal-day])
                         :values     (case month
                                       1 (range 1 29)
                                       (10 8 5 3) (range 1 31)
                                       (range 1 32))
                         :value      day
                         :on-change  (fn [day] (on-change (assoc config :verdict-default-config.date-input/day day)))
                         :required   true}]]
     [:div
      [:div.inline-flex.gap-4
       [ui/number-input (util/mmap {:id                 (str id "-count")
                                    :label-text         (tr [(rule->amount-tr rule)])
                                    :step               1
                                    :value              amount
                                    :on-change          (fn [ev]
                                                          (on-change (->> ev
                                                                          futil/tval
                                                                          parse-long
                                                                          (assoc config :verdict-default-config.date-input/amount))))
                                    :hide-unit?         true
                                    :wrapper-class-name "w-56"})]
       [ui/number-input (util/mmap {:id                 (str id "-time-window-years")
                                    :label-text         (tr [:verdict-default-config.date-input/years-after])
                                    :step               1
                                    :required           true
                                    :value              years
                                    :on-change          (fn [ev]
                                                          (on-change (->> ev
                                                                          futil/tval
                                                                          parse-long
                                                                          (assoc config :verdict-default-config.date-input/years))))
                                    :hide-unit?         true
                                    :wrapper-class-name "w-56"})]
       [cc/checkbox {:id                 (str id "-backdating-checkbox")
                     :label-text         (tr [:verdict-default-config.date-input/no-backdating])
                     :on-change          (fn [new-value]
                                           (on-change (assoc config :verdict-default-config.date-input/no-backdating? new-value)))
                     :checked            (boolean no-backdating?)
                     :wrapper-class-name "min-w-full place-self-center"}]]]
     [ui/help (str (tr [:group-admin/licence-renewal-days-help])
                   (tr [:group-admin/licence-renewal-days-help-example])
                   (tr [:group-admin/licence-renewal-days-help-licensing]))]]
    [:span
     [:div (tr [rule])]
     [:div.my-1 (str (tr [:group-admin/licence-renewal-date]) ": " day "." (+ 1 month) ".")]
     [:div.my-1 (str (tr [:group-admin/licence-renewal-time-window]) ": " amount " day(s), " years " year(s)")]]))

(defmethod date-input-editor :default
  [rule tr on-change amount config id]
  (if on-change
    [:div.inline-flex.gap-4
     [ui/number-input (util/mmap {:id         (str id "-count")
                                  :label-text (tr [(rule->amount-tr rule)])
                                  :step       1
                                  :required   true
                                  :value      amount
                                  :on-change  (fn [ev]
                                                (on-change (->> ev
                                                                futil/tval
                                                                parse-long
                                                                (assoc config
                                                                       :verdict-default-config.date-input/amount))))})]]
    [:span (tr [rule]) ": " (str amount)]))

(defmethod editor-for-type :verdict.section.type/date-input
  [{:keys [tr on-change permit-type level]}
   {:verdict-default-config.date-input/keys [rule amount month day years no-backdating? reference-date]
    :as                                     config}]
  (let [id (-> config config-model/verdict-default-identity str)]
    [:div.inline-flex.flex-col.gap-4
     (when (and (= permit-type :permit-type/L)
                (= level :group-config.level/work-type)
                (fn? on-change))
       [:label (tr [:verdict-default-config.date-input/reference-date])
        [ui-radio/radio {:name       (str id "-reference-date-radio-buttons")
                         :class-name "flex-wrap"
                         :values     licence-model/reference-date-values
                         :value      (or reference-date licence-model/default-reference-date-value)
                         :val->str   (comp tr vector)
                         :on-change  (fn [new-value]
                                       (on-change (assoc config
                                                         :verdict-default-config.date-input/reference-date
                                                         new-value)))}]])
     (when (fn? on-change)
       [ui-select/select {:id           (str id "-rule")
                          :required     true
                          :allow-empty? false
                          :label-text   (tr [:verdict-default-config.date-input/rule])
                          :values       verdict-section/date-input-default-rules
                          :val->str     (comp tr vector)
                          :value        rule
                          :on-change    (fn [new-value]
                                          (on-change (assoc config
                                                            :verdict-default-config.date-input/rule
                                                            new-value)))}])
     [date-input-editor
      rule tr on-change amount config month day id years no-backdating?]]))

(defn- user-id [user-or-id]
  (cond-> user-or-id
    (associative? user-or-id) :db/id))

(defmethod editor-for-type :verdict.section.type/authority-user
  [{:keys [tr on-change group-id]}
   {:verdict-default-config/keys [authority-user]
    :as                          config}]
  (let [id            (-> config config-model/verdict-default-identity str)
        users         (<sub [::authority-user-default-candidates-for-group group-id])
        selected-user (user-id authority-user)
        user->name    #(user-model/full-name %)]
    (if (fn? on-change)
      [ui-select/select {:id           (str id "-user")
                         :required     true
                         :allow-empty? true
                         :label-text   (tr [:verdict-default-config/authority-user])
                         :empty-text   (tr [:verdict-default-config/select-authority-user])
                         :values       (map :db/id users)
                         :val->str     (fn [user-id]
                                         (some-> (p/find-first users {:db/id user-id})
                                                 user->name))
                         :value        selected-user
                         :on-change    (fn [new-value]
                                         (on-change
                                           (assoc config
                                                  :verdict-default-config/authority-user
                                                  new-value)))}]
      [:span (or (some-> (p/find-first users {:db/id selected-user})
                         user->name)
                 (when (associative? authority-user)
                   (user-model/full-name authority-user)))])))

(defn section->str-fn [lang tr]
  (fn [[[_ section-type] names]]
    (let [superdomain-group-name (:superdomain-group-name names)
          lang-names             (dissoc names :superdomain-group-name)]
      (str (i18n-model/get-text-from-entities-or-map lang-names lang)
           " (" (tr [section-type]) ")"
           (when superdomain-group-name
             (str " (" superdomain-group-name ")"))))))

(defn- express-issuance-required-sections
  "Returns a set of [section-id section-type] tuples that must have verdict defaults
   because they are input sections of the selected express issuance template."
  [group-config parent-config]
  (let [express-issuance? (or (:group.config/express-issuance? group-config)
                              (:group.config/express-issuance? parent-config))
        template-id       (or (:group.config/express-issuance-template group-config)
                              (-> parent-config :group.config/express-issuance-template vals last :db/id))]
    (when (and express-issuance? template-id)
      (some->> (<sub [::entities/entity template-id])
               eu/descendants
               (filter #(and (contains? verdict-section/input-types (:verdict.section/type %))
                             (contains? verdict-section/verdict-sections-with-defaults (:verdict.section/type %))))
               (map (juxt :verdict.section/id :verdict.section/type))
               set))))

(defn- visible-default-configs [authority-user-defaults? configs]
  (cond->> configs
    (not authority-user-defaults?)
    (remove #(= :verdict.section.type/authority-user (:verdict-default-config/applies-to %)))))

(defn- nearest-default-configs [configs]
  (util/distinct-by config-model/verdict-default-identity configs))

(defn- lower-config-path [target lang config]
  (->> config
       eu/with-ancestors
       (take-while #(not= target (:db/id %)))
       (map #(config-model/localized-name % lang))
       reverse
       (str/join " / ")))

(defn editor [{:keys [tr *group-config group-id lang parent-config permit-type level target]}]
  (let [section->str                  (section->str-fn lang tr)
        group-config                  @*group-config
        lower-level-configurations    (<sub [::child-config-configurations target])
        current-default-identities    (->> group-config
                                           :group.config/verdict-defaults
                                           (map config-model/verdict-default-identity)
                                           set)
        authority-user-defaults?      (<sub [::ff/enabled? :feature-flag.id/express-issuance])
        express-issuance-requirements (when authority-user-defaults?
                                        (express-issuance-required-sections group-config parent-config))]
    [:div.mb-4
     (for-frag [{:verdict-default-config/keys [section-id applies-to]
                 :as                          conf} (visible-default-configs
                                                      authority-user-defaults?
                                                      (nearest-default-configs
                                                        (:group.config/verdict-defaults parent-config)))]
       (let [identity [section-id applies-to]]
         [:div.flex.flex-col.gap-4.items-baseline
          [:h6 (str (section->str (<sub [::section-def group-id section-id applies-to])) " - "
                    (tr [(if (contains? current-default-identities identity)
                           :verdict-default-config/overridden
                           :verdict-default-config/inherited)]))]
          [editor-for-type (util/mmap tr permit-type level group-id) conf]]))
     (for-frag [[[section-id applies-to :as identity] configs] (sort-by key lower-level-configurations)]
       (let [config-paths (->> configs
                               (map (partial lower-config-path target lang))
                               distinct
                               sort
                               (str/join ", "))]
         [:div.flex.items-baseline.gap-4
          [:h6 (str (section->str (<sub [::section-def group-id section-id applies-to])) " - "
                    (tr [(if (contains? current-default-identities identity)
                           :verdict-default-config/overridden-in
                           :verdict-default-config/configured-on-lower-level)]
                        [config-paths]))]]))
     (for-frag [{:verdict-default-config/keys [section-id applies-to]
                 :as                          conf} (visible-default-configs
                                                      authority-user-defaults?
                                                      (:group.config/verdict-defaults group-config))]
       (let [same-e?                         #(= (config-model/verdict-default-identity conf)
                                                 (config-model/verdict-default-identity %))
             restricted-by-express-issuance? (contains? express-issuance-requirements [section-id applies-to])
             on-change                       (fn [new-config]
                                               (swap! *group-config
                                                      update :group.config/verdict-defaults
                                                      (fn [all-default-configs]
                                                        (->> all-default-configs
                                                             (remove same-e?)
                                                             (cons new-config)
                                                             (sort-by config-model/verdict-default-identity)))))]
         [:div.flex.flex-col
          [:div.flex.items-baseline.gap-4
           [:h6 (section->str (<sub [::section-def group-id section-id applies-to]))]
           (cc/button {:kind     :ghost
                       :icon     i/remove
                       :disabled restricted-by-express-issuance?
                       :on-click (fn [_]
                                   (swap! *group-config
                                          update :group.config/verdict-defaults
                                          (partial remove same-e?)))}
                      (tr [:remove]))]
          (when restricted-by-express-issuance?
            [ui/help (tr [:verdict-default-config/required-by-express-issuance])])
          [editor-for-type (util/mmap tr on-change permit-type level group-id) conf]]))]))

(defn add-new [{:keys [target group-id tr add-item lang already-selected-configs]}]
  (let [available-values (<sub [::addable-sections-for-group target group-id already-selected-configs])]
    [:<>
     [ui-config-pills/add-component
      (util/mmap
        {:id              (str "verdict-default-" target)
         :val->str        (section->str-fn lang tr)
         :add-button-text (tr [:verdict/add-default-value-config])}
        tr add-item available-values)]
     (when (and (empty? available-values) (empty? already-selected-configs))
       [ui/help
        (tr [:verdict-default-config/none-available])
        {:color :blue}])]))

(defn handle-on-add [*group-config [[section-id section-type] _]]
  (swap! *group-config
         update
         :group.config/verdict-defaults
         (fn [configs]
           (->> (concat configs
                        [{:verdict-default-config/applies-to section-type
                          :verdict-default-config/section-id section-id}])
                (sort-by config-model/verdict-default-identity)))))

(defn get-reserved-keys [config]
  (->> config
       :group.config/verdict-defaults
       (map config-model/verdict-default-identity)
       set))

(defn defaults-conf [{:keys [tr group-id target *group-config parent-config permit-type level]}]
  (r/with-let [on-add-fn (futil/make-callback-fn handle-on-add)]
    (when (seq (<sub [::configurable-sections-for-group group-id]))
      (let [lang (<sub [:language])]
        (cc/form-group
          {:legend-text (tr [:verdict/default-values])}
          (if (<sub [::loading? group-id])
            (cc/loading-spinner {:with-overlay false})
            [:div
             [editor (util/mmap tr *group-config group-id lang parent-config permit-type level target)]
             [add-new (util/mmap {:add-item                 (on-add-fn *group-config)
                                  :already-selected-configs (get-reserved-keys @*group-config)}
                                 target group-id tr lang)]]))))))

(defn verdict-defaults-valid? [group-config]
  (->> group-config
       :group.config/verdict-defaults
       (every? verdict-model/verdict-default-config-valid?)))
